import { BaseHttpClient, baseOk, ApiOk, handle, buildUrl } from "./base";
import {
  photoCategoriesResponseSchema,
  photoCategoryMutationResponseSchema,
  photoCategoryDeleteResponseSchema,
  photoCategoryReorderResponseSchema,
  photoLikeResponseSchema,
  photoLikesResponseSchema,
  photosListResponseSchema,
  photoViewsRecordBodySchema,
  photoViewsRecordResponseSchema,
} from "@openings/api-contract/photos";
import type {
  PhotoViewsRecordBody,
  PhotoViewsRecordResponse,
  PhotoCategoriesQuery,
  PhotoCategoriesResponse,
  PhotoCategory,
  PhotoCategoryCreate,
  PhotoCategoryUpdate,
  PhotoCategoryDelete,
  PhotoCategoryReorder,
  PhotoCategoryMutationResponse,
  PhotoCategoryDeleteResponse,
  PhotoCategoryReorderResponse,
  PhotoItem,
  PhotoLikeResponse,
  PhotoLikesQuery,
  PhotoLikesResponse,
  PhotosListQuery,
  PhotosListResponse,
} from "@openings/api-contract/photos";

const photosResponse = baseOk(photosListResponseSchema);
const photoCategoriesResponse = baseOk(photoCategoriesResponseSchema);
const photoCategoryMutationResp = baseOk(photoCategoryMutationResponseSchema);
const photoCategoryDeleteResp = baseOk(photoCategoryDeleteResponseSchema);
const photoCategoryReorderResp = baseOk(photoCategoryReorderResponseSchema);
const photoLikeResp = baseOk(photoLikeResponseSchema);
const photoLikesResp = baseOk(photoLikesResponseSchema);

export type PhotosListData = PhotosListResponse;
export type PhotosListResult = ApiOk<PhotosListResponse>;
export type PhotoCategoriesData = PhotoCategoriesResponse;
export type PhotoCategoriesResult = ApiOk<PhotoCategoriesResponse>;
export type PhotoCategoryMutationResult = ApiOk<PhotoCategoryMutationResponse>;
export type PhotoCategoryDeleteResult = ApiOk<PhotoCategoryDeleteResponse>;
export type PhotoCategoryReorderResult = ApiOk<PhotoCategoryReorderResponse>;
export type PhotoLikeResult = ApiOk<PhotoLikeResponse>;
export type PhotoLikesResult = ApiOk<PhotoLikesResponse>;

export type PhotosListInput = PhotosListQuery;
export type { PhotoItem };
export type { PhotoCategory };

export class PhotosClient extends BaseHttpClient {
  /** Anonymous, retry-safe engagement batch. Kept alive during navigation. */
  async recordViews(
    input: PhotoViewsRecordBody,
  ): Promise<ApiOk<PhotoViewsRecordResponse>> {
    const body = photoViewsRecordBodySchema.parse(input);
    const response = await this.fetcher(
      buildUrl(this.base, "/v1/photos/views"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        keepalive: true,
        body: JSON.stringify(body),
      },
    );
    return handle(response, baseOk(photoViewsRecordResponseSchema));
  }

  list(input: PhotosListInput): Promise<PhotosListResult> {
    const qs = new URLSearchParams();
    if (input.limit) qs.set("limit", String(input.limit));
    if (input.cursor) qs.set("cursor", input.cursor);
    if (input.category) qs.set("category", input.category);
    if (input.salonId) qs.set("salonId", input.salonId);
    if (input.pinned) qs.set("pinned", "true");
    if (input.deviceId) qs.set("deviceId", input.deviceId);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return this.get(`/v1/photos${suffix}`, photosResponse);
  }

  /** Like a photo for the given device (idempotent). */
  likePhoto(photoId: string, deviceId: string): Promise<PhotoLikeResult> {
    return this.post(
      `/v1/photos/${encodeURIComponent(photoId)}/like`,
      { deviceId },
      photoLikeResp,
    );
  }

  /** Remove a device's like from a photo (idempotent). */
  unlikePhoto(photoId: string, deviceId: string): Promise<PhotoLikeResult> {
    return this.post(
      `/v1/photos/${encodeURIComponent(photoId)}/unlike`,
      { deviceId },
      photoLikeResp,
    );
  }

  /** List photo ids a device has liked within a salon. */
  listLikes(input: PhotoLikesQuery): Promise<PhotoLikesResult> {
    const qs = new URLSearchParams();
    qs.set("salonId", input.salonId);
    qs.set("deviceId", input.deviceId);
    return this.get(`/v1/photos/likes?${qs.toString()}`, photoLikesResp);
  }

  listCategories(input: PhotoCategoriesQuery): Promise<PhotoCategoriesResult> {
    const qs = new URLSearchParams();
    qs.set("salonId", input.salonId);
    return this.get(
      `/v1/photos/categories?${qs.toString()}`,
      photoCategoriesResponse,
    );
  }

  createCategory(
    input: PhotoCategoryCreate,
  ): Promise<PhotoCategoryMutationResult> {
    return this.post("/v1/photos/categories", input, photoCategoryMutationResp);
  }

  updateCategory(
    input: PhotoCategoryUpdate,
  ): Promise<PhotoCategoryMutationResult> {
    return this.post(
      "/v1/photos/categories/update",
      input,
      photoCategoryMutationResp,
    );
  }

  deleteCategory(
    input: PhotoCategoryDelete,
  ): Promise<PhotoCategoryDeleteResult> {
    return this.post(
      "/v1/photos/categories/delete",
      input,
      photoCategoryDeleteResp,
    );
  }

  reorderCategories(
    input: PhotoCategoryReorder,
  ): Promise<PhotoCategoryReorderResult> {
    return this.post(
      "/v1/photos/categories/reorder",
      input,
      photoCategoryReorderResp,
    );
  }
}
